import * as React from "react";
import {
  List, Datagrid, TextField, FunctionField, BooleanField, NumberField,
  Show, SimpleShowLayout, Edit, Create, SimpleForm,
  TextInput, BooleanInput, NumberInput, SearchInput, required,
  BulkDeleteButton, DeleteButton, EditButton, TopToolbar,
} from "react-admin";
import { Chip, Box } from "@mui/material";
import { JsonField } from "../../components/JsonField";
import { TechInfo } from "../../components/ui";
import { EnumChip } from "../../components/EnumChip";
import { SUGGESTION_STATUS } from "../../enums";

// SpecialityDTO réels : id, name, domainMedical, keywords (KeywordDTO[]),
// color (hex), job, status (PENDING/VALIDATED/REJECTED), suggestedBy,
// suggestedByName, suggestedByEmail, createdAt, presentielOnly,
// averageDurationMinutes.
// CRUD via UserController : POST/PUT/DELETE /users/specialities[/{id}].

const filters = [<SearchInput key="q" source="q" alwaysOn />];

// Suppression en mode "pessimistic" : l'appel DELETE part immédiatement
// (après confirmation) et la ligne ne disparaît qu'une fois le back OK.
// En mode "undoable" (défaut react-admin), l'appel n'était envoyé qu'à la
// fermeture du bandeau « Annuler » : recharger ou changer de page l'annulait,
// et la spécialité réapparaissait.
const BulkActions = () => (
  <BulkDeleteButton mutationMode="pessimistic" />
);

const ShowActions = () => (
  <TopToolbar>
    <EditButton />
    <DeleteButton mutationMode="pessimistic" />
  </TopToolbar>
);

const KeywordsField = () => (
  <FunctionField label="Mots-clés" render={(r) => {
    const kw = Array.isArray(r?.keywords) ? r.keywords : [];
    if (kw.length === 0) return "—";
    return (
      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
        {kw.slice(0, 8).map((k, i) => <Chip key={i} size="small" label={k?.name || k} />)}
        {kw.length > 8 && <Chip size="small" variant="outlined" label={`+${kw.length - 8}`} />}
      </Box>
    );
  }} />
);

const ColorSwatch = () => (
  <FunctionField label="Couleur" render={(r) => r?.color ? (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Box sx={{ width: 18, height: 18, borderRadius: 0.5, bgcolor: r.color, border: "1px solid rgba(0,0,0,0.1)" }} />
      <span style={{ fontFamily: "monospace", fontSize: 12 }}>{r.color}</span>
    </Box>
  ) : "—"} />
);

export default {
  list: () => (
    <List filters={filters} sort={{ field: "name", order: "ASC" }} perPage={50}>
      <Datagrid rowClick="show" bulkActionButtons={<BulkActions />}>
        <TextField source="name" label="Spécialité" />
        <TextField source="domainMedical" label="Domaine médical" />
        <TextField source="job" label="Métier" />
        <ColorSwatch />
        <KeywordsField />
        <BooleanField source="presentielOnly" label="Présentiel uniquement" />
        <NumberField locales="fr-FR" source="averageDurationMinutes" label="Durée moy. (min)" />
        <EnumChip source="status" label="Statut" enumObj={SUGGESTION_STATUS} />
      </Datagrid>
    </List>
  ),
  show: () => (
    <Show actions={<ShowActions />}>
      <SimpleShowLayout>
        <TextField source="name" />
        <TextField source="domainMedical" />
        <TextField source="job" />
        <ColorSwatch />
        <KeywordsField />
        <BooleanField source="presentielOnly" />
        <NumberField locales="fr-FR" source="averageDurationMinutes" />
        <TextField source="status" />
        <JsonField source="__all__" />
        <TechInfo />
        </SimpleShowLayout>
    </Show>
  ),
  edit: () => (
    <Edit mutationMode="pessimistic">
      <SimpleForm>
        <TextInput source="name" label="Nom" validate={required()} />
        <TextInput source="domainMedical" label="Domaine médical" />
        <TextInput source="job" label="Métier rattaché" />
        <TextInput source="color" label="Couleur (#hex)" />
        <BooleanInput source="presentielOnly" label="Présentiel uniquement" />
        <NumberInput source="averageDurationMinutes" label="Durée moyenne (min)" />
      </SimpleForm>
    </Edit>
  ),
  create: () => (
    <Create>
      <SimpleForm>
        <TextInput source="name" label="Nom" validate={required()} />
        <TextInput source="domainMedical" label="Domaine médical" />
        <TextInput source="job" label="Métier rattaché" />
        <TextInput source="color" label="Couleur (#hex)" defaultValue="#0d7377" />
        <BooleanInput source="presentielOnly" label="Présentiel uniquement" defaultValue={false} />
        <NumberInput source="averageDurationMinutes" label="Durée moyenne (min)" defaultValue={45} />
      </SimpleForm>
    </Create>
  ),
};
